// HT-3/HT-4.5 · 書類読み取り学習レポート（月次・自動保存 + 手動の書き出し）
import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { DownloadSimple, ArrowRight } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { LEARN, authHeader, detailOf, Panel, Pill, Collapse } from './shared';

const csvOf = (d) => {
  const lines = [['項目', '値']];
  lines.push(['対象月', d.month]);
  lines.push(['承認した学習候補', d.approved_proposals]);
  lines.push(['却下した学習候補', d.rejected_proposals]);
  lines.push(['例が不足', d.needs_examples]);
  lines.push(['後で確認', d.deferred_proposals]);
  lines.push(['有効にした学習内容', d.activated_versions]);
  lines.push(['有効なバージョン', d.active_version ?? '']);
  lines.push(['検証の状況', d.validation_status_ja]);
  lines.push(['判読できない書類', d.unreadable_documents]);
  lines.push(['まだ使われていない学習内容', d.unused_rules ?? 0]);
  (d.metrics || []).forEach((m) => lines.push([
    `測定: ${m.label_ja}`, `${m.before ?? '—'} → ${m.after ?? '—'}（${m.change_ja}）`]));
  (d.coverage || []).forEach((c) => lines.push([
    `実書類での確認: ${c.label_ja}`, c.status_label_ja]));
  (d.todos || []).forEach((t) => lines.push(['宿題', t.text_ja]));
  return lines.map((r) => r.map((v) => `"${String(v ?? '')}"`).join(',')).join('\r\n');
};

export default function LearningDigest({ onGo }) {
  const [d, setD] = useState(null);
  const [history, setHistory] = useState(null);
  const [month, setMonth] = useState('');

  const load = useCallback(async (m) => {
    try {
      const [r, h] = await Promise.all([
        axios.get(`${LEARN}/digest`, { headers: authHeader(), params: m ? { month: m } : {} }),
        axios.get(`${LEARN}/digests`, { headers: authHeader() }),
      ]);
      setD(r.data);
      setHistory(h.data);
      setMonth(r.data.month);
    } catch (e) { toast.error(detailOf(e, 'レポートを取得できませんでした')); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const blob = new Blob([`\uFEFF${csvOf(d)}`], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `書類読み取り学習レポート_${d.month}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
    toast.success('レポートを書き出しました');
  };

  if (!d) return null;
  return (
    <Panel title={d.title_ja} testId="learn-digest"
      hint="毎月の学習の状況です。毎月自動で保存されます。過去の書類は再読み取りするまで変わりません。"
      action={(
        <div className="flex flex-wrap items-center gap-1.5">
          <select value={month} aria-label="対象月をえらぶ"
            onChange={(e) => { setMonth(e.target.value); load(e.target.value); }}
            className="h-9 rounded-md border border-zinc-200 px-2 text-[12px]"
            data-testid="digest-month-select">
            {[d.month, ...(history?.rows || []).map((r) => r.month)]
              .filter((m, i, a) => m && a.indexOf(m) === i)
              .map((m) => <option key={m} value={m}>{m}</option>)}
          </select>
          <Button size="sm" variant="outline" className="h-9 text-[12px]" onClick={exportCsv}
            data-testid="digest-export">
            <DownloadSimple size={12} className="mr-1" />書き出す
          </Button>
        </div>
      )}>
      <p className="flex flex-wrap gap-1.5" data-testid="digest-summary">
        <Pill testId="digest-month">{d.month}</Pill>
        <Pill tone="teal">承認 {d.approved_proposals}件</Pill>
        <Pill tone="rose">却下 {d.rejected_proposals}件</Pill>
        <Pill tone="amber">追加例が必要 {d.needs_examples}件</Pill>
        <Pill>後で確認 {d.deferred_proposals}件</Pill>
        <Pill>有効にした学習内容 {d.activated_versions}件</Pill>
        <Pill>{d.validation_status_ja}</Pill>
        <Pill>判読できない書類 {d.unreadable_documents}件</Pill>
        <Pill tone={d.unused_rules ? 'amber' : 'zinc'}>
          まだ使われていない学習内容 {d.unused_rules ?? 0}件
        </Pill>
      </p>

      <div className="pt-3">
        <p className="pb-1 text-[12px] font-medium text-zinc-800">今月の宿題</p>
        {(d.todos || []).length === 0 ? (
          <p className="text-[11.5px] text-teal-800" data-testid="digest-todos-empty">
            片付けるべき宿題はありません。{d.todos_note_ja}
          </p>
        ) : (
          <div className="space-y-1.5" data-testid="digest-todos">
            {d.todos.map((t) => (
              <div key={t.key}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-zinc-200 px-3 py-2"
                data-testid={`digest-todo-${t.key}`}>
                <p className="text-[12px] text-zinc-800">{t.text_ja}</p>
                {t.area === 'responsibilities' ? (
                  <a href="/documents/responsibilities"
                    className="inline-flex min-h-[36px] items-center rounded-md bg-teal-700 px-3 text-[12px] text-white hover:bg-teal-800"
                    data-testid={`digest-todo-go-${t.key}`}>
                    {t.action_label_ja}<ArrowRight size={12} className="ml-1" />
                  </a>
                ) : (
                  <Button size="sm" className="h-9 bg-teal-700 text-[12px] hover:bg-teal-800"
                    onClick={() => onGo?.(t.area)} data-testid={`digest-todo-go-${t.key}`}>
                    {t.action_label_ja}<ArrowRight size={12} className="ml-1" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {(d.metrics || []).length > 0 ? (
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[380px] text-[11.5px]" data-testid="digest-metrics">
            <thead>
              <tr className="text-left text-zinc-500">
                <th className="py-1 font-medium">測った内容</th>
                <th className="py-1 font-medium">これまで</th>
                <th className="py-1 font-medium">有効にした後</th>
                <th className="py-1 font-medium">変化</th>
              </tr>
            </thead>
            <tbody>
              {d.metrics.map((m) => (
                <tr key={m.label_ja} className="border-t border-zinc-100">
                  <td className="py-1 text-zinc-700">{m.label_ja}</td>
                  <td className="py-1 tabular-nums text-zinc-600">{m.before ?? '—'}</td>
                  <td className="py-1 tabular-nums text-zinc-800">{m.after ?? '—'}</td>
                  <td className={`py-1 ${m.change_ja === '改善' ? 'text-teal-700'
                    : m.change_ja === '悪化' ? 'text-rose-700' : 'text-zinc-500'}`}>
                    {m.change_ja}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <p className="pt-2 text-[11.5px] text-zinc-500" data-testid="digest-no-metrics">
          有効にした学習内容がないため、比較する数値はまだありません。
        </p>
      )}

      <div className="pt-2">
        <Collapse label="実書類での確認状況" testId="digest-coverage">
          {(d.coverage || []).map((c) => (
            <p key={c.profile_key} className="text-[11.5px] text-zinc-700">
              {c.label_ja}: {c.status_label_ja}（実書類 {c.real_document_count}件）
            </p>
          ))}
        </Collapse>
      </div>
      <div className="pt-1.5">
        <Collapse label={`保存済みのレポート（${history?.count ?? 0}件）`}
          testId="digest-history">
          {(history?.rows || []).length === 0 ? (
            <p className="text-[11.5px] text-zinc-600">
              保存済みのレポートはまだありません（毎月自動で保存されます）。
            </p>
          ) : (history.rows || []).map((r) => (
            <button key={r.month} type="button"
              onClick={() => { setMonth(r.month); load(r.month); }}
              className="block min-h-[32px] w-full text-left text-[11.5px] text-zinc-700 hover:underline"
              data-testid={`digest-history-${r.month}`}>
              {r.month}: 承認 {r.approved_proposals}件 · 有効にした学習内容 {r.activated_versions}件
              {' '}· 判読できない書類 {r.unreadable_documents}件 · {r.validation_status_ja}
            </button>
          ))}
          <p className="pt-1 text-[11px] text-zinc-500">{history?.notice_ja}</p>
        </Collapse>
      </div>
      <p className="pt-2 text-[11px] text-zinc-500">{d.notice_ja}</p>
    </Panel>
  );
}
